import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Building2, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { formatFinanceStatus } from '@/hooks/useFinanceiroPage'
import type { GestorClienteDetail } from '@/services/gestoresService'
import { cn } from '@/utils/cn'

type TabId = 'cadastro' | 'escopo' | 'historico' | 'chamados'

const TABS: { id: TabId; label: string }[] = [
  { id: 'cadastro', label: 'Cadastro' },
  { id: 'escopo', label: 'Escopo vendido' },
  { id: 'historico', label: 'Histórico de fluxos' },
  { id: 'chamados', label: 'Chamados' },
]

type Props = {
  open: boolean
  loading: boolean
  detail: GestorClienteDetail | null
  onClose: () => void
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex flex-col gap-0.5 sm:flex-row sm:gap-3'>
      <span className='shrink-0 text-sm text-text-muted sm:w-36'>{label}</span>
      <span className='text-sm font-medium text-text-primary'>{value}</span>
    </div>
  )
}

export function GestorClienteDetailModal({ open, loading, detail, onClose }: Props) {
  const [tab, setTab] = useState<TabId>('cadastro')

  useEffect(() => {
    if (open) setTab('cadastro')
  }, [open, detail?.id])

  if (!open) return null

  return createPortal(
    <AnimatePresence>
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className='absolute inset-0 bg-black/60 backdrop-blur-sm'
          onClick={onClose}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          className='relative z-10 flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-[var(--glass-border)] bg-[var(--bg-card)] shadow-2xl'
        >
          <div className='flex items-center justify-between border-b border-[var(--glass-border)] p-5'>
            <div className='flex items-center gap-3'>
              <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--brand-primary-muted)]'>
                <Building2 size={18} className='text-[var(--brand-primary)]' />
              </div>
              <div>
                <h2 className='text-lg font-semibold text-text-primary'>
                  {detail?.nome ?? 'Cliente'}
                </h2>
                <p className='text-xs text-text-muted'>Visão gestor — sem valores comerciais</p>
              </div>
            </div>
            <button
              type='button'
              onClick={onClose}
              className='rounded-md p-1.5 text-text-muted hover:bg-bg-secondary hover:text-text-primary'
            >
              <X size={16} />
            </button>
          </div>

          <div className='flex overflow-x-auto border-b border-[var(--glass-border)] px-5'>
            {TABS.map((t) => (
              <button
                key={t.id}
                type='button'
                onClick={() => setTab(t.id)}
                className={cn(
                  'shrink-0 px-4 py-3 text-sm font-medium transition-colors',
                  tab === t.id ? 'text-accent' : 'text-text-muted hover:text-text-primary',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className='min-h-0 flex-1 overflow-y-auto p-5 text-sm'>
            {loading || !detail ? (
              <div className='flex justify-center py-16'>
                <div className='h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent' />
              </div>
            ) : tab === 'cadastro' ? (
              <div className='space-y-3 rounded-xl border border-border bg-bg-secondary/30 p-4'>
                <InfoRow label='Razão social' value={detail.razaoSocial ?? detail.nome} />
                <InfoRow label='Nome fantasia' value={detail.nome} />
                <InfoRow label='CNPJ' value={detail.cnpj ?? '—'} />
                <InfoRow label='E-mail' value={detail.email ?? '—'} />
                <InfoRow label='Telefone' value={detail.telefone ?? '—'} />
                {detail.clienteAtivos.map((ca) => (
                  <div key={ca.id} className='mt-3 rounded-lg border border-border bg-bg-primary p-3'>
                    <p className='font-medium'>{ca.deal?.nome ?? 'Cliente ativo'}</p>
                    <p className='text-xs text-text-muted'>Closer: {ca.closer.name}</p>
                  </div>
                ))}
              </div>
            ) : tab === 'escopo' ? (
              <div className='space-y-4'>
                {detail.projetos.length === 0 ? (
                  <p className='text-text-muted'>Nenhum projeto com escopo registrado.</p>
                ) : (
                  detail.projetos.map((p) => (
                    <div key={p.id} className='rounded-xl border border-border bg-bg-secondary/30 p-4'>
                      <p className='font-semibold text-text-primary'>{p.name ?? 'Projeto'}</p>
                      <p className='mt-1 text-xs text-text-muted'>
                        {formatFinanceStatus(p.status)} · {p.source.replace(/_/g, ' ')}
                      </p>
                      {p.escopo.plataforma && (
                        <InfoRow label='Plataforma' value={String(p.escopo.plataforma)} />
                      )}
                      {p.escopo.servicosContratados && (
                        <div className='mt-3'>
                          <p className='text-xs font-semibold uppercase text-text-muted'>
                            Serviços contratados
                          </p>
                          <p className='mt-1 whitespace-pre-wrap text-text-primary'>
                            {String(p.escopo.servicosContratados)}
                          </p>
                        </div>
                      )}
                      {p.escopo.description && (
                        <div className='mt-3'>
                          <p className='text-xs font-semibold uppercase text-text-muted'>Descrição</p>
                          <p className='mt-1 whitespace-pre-wrap'>{String(p.escopo.description)}</p>
                        </div>
                      )}
                      {p.escopo.responsavelAssinatura && (
                        <InfoRow label='Resp. assinatura' value={String(p.escopo.responsavelAssinatura)} />
                      )}
                    </div>
                  ))
                )}
              </div>
            ) : tab === 'historico' ? (
              <ul className='space-y-2'>
                {detail.historicoFluxos.length === 0 ? (
                  <li className='text-text-muted'>Sem movimentações registradas.</li>
                ) : (
                  detail.historicoFluxos.map((h) => (
                    <li
                      key={`${h.tipo}-${h.id}`}
                      className='rounded-lg border border-border bg-bg-secondary/30 px-3 py-2.5'
                    >
                      <div className='flex items-start justify-between gap-2'>
                        <div>
                          <p className='font-medium text-text-primary'>{h.titulo}</p>
                          <p className='text-xs text-text-muted'>{h.detalhe}</p>
                        </div>
                        <span className='shrink-0 text-[10px] uppercase text-text-muted'>
                          {h.tipo}
                        </span>
                      </div>
                      <p className='mt-1 text-[11px] text-text-muted'>
                        {new Date(h.data).toLocaleString('pt-BR')}
                      </p>
                    </li>
                  ))
                )}
              </ul>
            ) : (
              <ul className='space-y-2'>
                {detail.chamados.length === 0 ? (
                  <li className='text-text-muted'>Nenhum chamado vinculado.</li>
                ) : (
                  detail.chamados.map((c) => (
                    <li
                      key={c.id}
                      className='rounded-lg border border-border bg-bg-secondary/30 px-3 py-2.5'
                    >
                      <p className='font-mono text-xs text-text-muted'>{c.protocol}</p>
                      <p className='font-medium'>{c.type.replace(/_/g, ' ')}</p>
                      <p className='text-xs text-text-muted'>{formatFinanceStatus(c.status)}</p>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body,
  )
}
